// SPDX-FileCopyrightText: Copyright (C) 2026 provide.io llc
// SPDX-License-Identifier: Apache-2.0
import type { ReactElement } from "react";
import { Link, useLocation } from "react-router";
import { useApp } from "@/root";
import { HeaderSearch } from "./HeaderSearch";
import { UserMenu } from "./UserMenu";

export function TopBar(): ReactElement {
  const { config } = useApp();
  const { pathname } = useLocation();
  // The home route IS the search — there's already a giant centered
  // input on the page. Don't duplicate it in the topbar.
  const isHome = pathname === "/";

  return (
    <header className="sticky top-0 z-10 border-border/70 border-b bg-header/95 backdrop-blur-md">
      {/* Single flex-wrap layout: at md+ the row stays on one line at
          h-14 (the original desktop chrome); on phones the search wraps
          to its own row below the brand + nav + user menu so the input
          gets the full viewport width instead of clipping. The wrap is
          driven by ``order-last w-full`` on the search container and
          undone on md+ with ``md:order-none md:w-auto md:flex-1``. */}
      <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-x-5 gap-y-2 px-6 py-2 md:h-14 md:flex-nowrap md:py-0">
        <Link
          to="/"
          aria-label={config.branding.title}
          className="shrink-0 font-[700] text-[18px] tracking-[-0.02em] hover:text-accent"
          // Tailwind's font-* utilities can't reference arbitrary CSS
          // vars; an inline style scopes Space Grotesk to just the
          // wordmark without polluting the rest of the type system.
          style={{ fontFamily: "var(--font-display)" }}
        >
          {config.branding.title}
        </Link>
        {!isHome && (
          <Link
            to="/pricing"
            className="shrink-0 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          >
            pricing
          </Link>
        )}
        {/* On mobile this row-2 search input is full-width and ordered
            last; on md+ it sits inline between pricing + the user menu
            and takes the available middle space. */}
        {!isHome && (
          <div className="order-last w-full min-w-0 md:order-none md:w-auto md:flex-1">
            <HeaderSearch />
          </div>
        )}
        <div className="ml-auto shrink-0 md:ml-0">
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
